import { Auth } from 'aws-amplify';
import appConfig from '../../../../config/app-config';

const { address: API_URL, key: API_KEY } = appConfig.apis.billing;

const getHeaders = async () => {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    return {
        'x-api-key': API_KEY,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
    };
};

// ─── Cancelamento (CANCEL_INVOICE) ──────────────────────────────────────────
// Sem boleto: cancela direto. Com boleto: dispara CANCEL_BILL ao biller e o
// invoice só é cancelado quando o BOLETO_CANCELED voltar (assíncrono).

export interface CancelInvoicePayload {
    reason?: string;
    actor_id?: string;
    actor_name?: string;
}

export interface CancelInvoiceResponse {
    invoiceId: string;
    result: 'CANCELLED' | 'CANCEL_BILL_REQUESTED' | 'ALREADY_CANCELLED';
}

export const cancelInvoice = async (invoiceId: number | string, payload: CancelInvoicePayload): Promise<CancelInvoiceResponse> => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/invoices/${invoiceId}/cancel`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    });
    if (!res.ok) {
        const errorBody = await res.json().catch(() => null);
        throw new Error(errorBody?.message || `Erro HTTP ${res.status}`);
    }
    return res.json();
};

// ─── Invoices por operação (modal da tela de Operações) ─────────────────────

export const listInvoicesByOperation = async (creditOperationId: string) => {
    const headers = await getHeaders();
    const params = new URLSearchParams({
        'filter[credit_operation_id]': creditOperationId,
        size: '200',
        sort: 'due_date',
    });
    const res = await fetch(`${API_URL}/invoices?${params.toString()}`, { headers });
    if (!res.ok) throw new Error(`Erro HTTP ${res.status}`);
    return res.json();
};

// ─── Histórico de ações de cobrança (billing_action) ────────────────────────

export const listInvoiceActions = async (invoiceId: number | string) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/invoices/${invoiceId}/actions`, { headers });
    if (!res.ok) throw new Error(`Erro HTTP ${res.status}`);
    return res.json();
};

// ─── Pagamento (POST /billing/payments) — AF-3327 ───────────────────────────
// Registra UM payment e dispara os eventos por invoice (REGISTER_INVOICE_PAYMENT /
// REGISTER_PAID_INVOICE + CANCEL_INVOICE/UNCOLLECT_INVOICE).

export interface PaymentPayer {
    external_id?: string;       // uuid do agregado person (quando encontrado no person service)
    name: string;
    document_number: string;
    address_street?: string;
    address_number?: string;
    address_complement?: string;
    address_neighborhood?: string;
    address_city?: string;
    address_state?: string;
    address_zip_code?: string;
    email?: string;
    phone?: string;
}

export interface PaymentInvoiceItem {
    credit_operation_id: string;
    charge_id: string | null;
    invoice_id: string;
    value: number;
    /** tri-state: true liquida o título, false mantém aberto, null = baldinho (só INSTALLMENT
     *  e garantia). O cenário do charge restringe — ver domain/charge_scenario no billing. */
    total_payment: boolean | null;
    cancel_invoice: boolean;
    uncollect: boolean;
}

export interface ProcessPaymentPayload {
    payment_id?: string;
    amount: number;
    payment_date: string;        // ISO 8601 (instante; o front converte wall-clock local → UTC)
    /** PIX | TRANSFER | BOLETO — só em payment_type=FINANCIAL */
    payment_method?: string;
    payment_type: string;        // FINANCIAL | DEBT_CONFESSION | ASSET_DATION
    payer?: PaymentPayer;
    payer_id?: string;
    /** obrigatório apenas em payment_type=FINANCIAL */
    receiving_bank_account_id?: string;
    user_responsible?: string;
    file_name_receipt?: string;
    file_url_receipt?: string;
    /** Fonte/sub-rogação/aval e dados do tipo de pagamento (consolidado com a baixa de título) */
    payment_source?: string;
    is_subrogation?: boolean;
    pending_guarantor_refund?: boolean;
    debt_confession_contract?: string;
    asset_dation_list?: unknown[];
    data?: Record<string, unknown>;
    invoices: PaymentInvoiceItem[];
}

export const processPayment = async (payload: ProcessPaymentPayload) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/payments`, {
        method: 'POST', headers, body: JSON.stringify(payload),
    });
    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || `Erro HTTP ${res.status}`);
    }
    return res.json();
};

// ─── Faturas de um charge (drill-in da tela de Cobrança — Títulos) ──────────
// O charge é a dupla (operação, charge_id): filtrar só por charge_id juntaria charges
// homônimos de operações diferentes, que são linhas distintas na tela.

export const listInvoicesByCharge = async (creditOperationId: string, chargeId: string) => {
    const headers = await getHeaders();
    const params = new URLSearchParams({
        'filter[credit_operation_id]': creditOperationId,
        'filter[charge_id]': chargeId,
        size: '200',
        sort: 'id',
    });
    const res = await fetch(`${API_URL}/invoices?${params.toString()}`, { headers });
    if (!res.ok) throw new Error(`Erro HTTP ${res.status}`);
    return res.json();
};

// ─── Descobrar (UNCOLLECT_INVOICE) ──────────────────────────────────────────
// Para de cobrar SEM cancelar: a fatura continua devida, só sai da cobrança.

export interface UncollectInvoiceResponse {
    invoiceId: string;
    collect: false;
    result: 'UNCOLLECTED' | 'ALREADY_UNCOLLECTED';
}

export const uncollectInvoice = async (invoiceId: number | string): Promise<UncollectInvoiceResponse> => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/invoices/${invoiceId}/uncollect`, { method: 'POST', headers });
    if (!res.ok) {
        const errorBody = await res.json().catch(() => null);
        throw new Error(errorBody?.message || `Erro HTTP ${res.status}`);
    }
    return res.json();
};

// ─── Gerar nova fatura a partir de outra (POST /invoices/<ref>/derive) ──────
// A fração de cada fatura é DERIVADA da quantidade enviada: o conjunto fecha o charge
// por construção. Cancelar/descobrar valem só para a fatura de origem.

export interface DeriveInvoiceItem {
    value: number;
    due_date?: string | null;
    with_boleto?: boolean;
    collect?: boolean | null;
    /** tri-state: true liquida, false mantém aberto, null = baldinho (só INSTALLMENT) */
    total_payment?: boolean | null;
    document_title?: string | null;
    notes?: string | null;
    bill_charges?: Record<string, unknown> | null;
}

export interface DeriveInvoicesPayload {
    invoices: DeriveInvoiceItem[];
    cancel_parent?: boolean;
    uncollect_parent?: boolean;
    reason?: string;
}

export const deriveInvoices = async (parentInvoiceId: string, payload: DeriveInvoicesPayload) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/invoices/${parentInvoiceId}/derive`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    });
    if (!res.ok) {
        const errorBody = await res.json().catch(() => null);
        throw new Error(errorBody?.message || `Erro HTTP ${res.status}`);
    }
    return res.json();
};
